import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import { ShoppingBag, LogOut, LayoutDashboard, Gamepad2, Menu, X, ArrowRight, LogIn, Compass } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close the drawer whenever the route changes (tap a link → drawer collapses).
  useEffect(() => { setDrawerOpen(false); }, [location.pathname, location.search]);

  // Trap body scroll while the drawer is open on mobile.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  return (
    <header className="sticky top-0 z-40 bg-[#FFFDF7]/85 backdrop-blur border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display text-xl sm:text-2xl font-semibold text-brandSlate whitespace-nowrap" data-testid="nav-logo">
          <span className="font-display">
            <span className="text-brandPink">My</span><span className="text-brandTeal">TableTop</span><span className="text-brandSlate">App</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-7 text-sm font-semibold text-brandSlate">
          <Link to="/browse" className="hover:text-brandPink transition inline-flex items-center gap-1.5" data-testid="nav-browse">
            Browse <span className="pill bg-brandTeal/10 text-brandTeal !text-[10px] !py-0.5 !px-1.5">No login needed</span>
          </Link>
          <Link to="/kidzone" className="hover:text-brandPink transition inline-flex items-center gap-1.5" data-testid="nav-kidzone">
            <Gamepad2 className="w-4 h-4" /> Kid Zone
          </Link>
          {user && (
            <>
              <Link to="/sell" className="hover:text-brandPink transition" data-testid="nav-sell">Sell a Toy</Link>
              <Link to="/orders" className="hover:text-brandPink transition" data-testid="nav-orders">Orders</Link>
              <Link to="/dashboard/parent" className="hover:text-brandPink transition inline-flex items-center gap-1" data-testid="nav-dashboard">
                <LayoutDashboard className="w-4 h-4" /> Dashboard
              </Link>
              {/* Iteration 250 — Parent Alert Bell lives directly after
                  Dashboard in the desktop header nav. */}
              <NotificationBell testIdPrefix="nav-bell" />
            </>
          )}
        </nav>
        <div className="flex items-center gap-3">
          {/* Desktop-only signed-in state */}
          {user && (
            <>
              <span className="hidden sm:inline text-sm text-slate-600" data-testid="nav-user-greeting">
                Hi, <strong className="text-brandSlate">{user.name || user.email}</strong>
              </span>
              <button onClick={async () => { await logout(); navigate("/"); }}
                className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-rose-600 transition"
                data-testid="nav-logout-btn">
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          )}
          {/* Desktop-only signed-out CTAs */}
          {!user && (
            <div className="hidden md:flex items-center gap-3">
              <Link to="/auth?mode=login" className="text-sm font-semibold text-brandSlate hover:text-brandPink whitespace-nowrap" data-testid="nav-login">Sign in</Link>
              <Link to="/auth?mode=register" className="btn-primary !px-5 !py-2.5 text-sm whitespace-nowrap" data-testid="nav-signup">Get started</Link>
            </div>
          )}
          {/* Mobile hamburger — replaces all inline mobile links */}
          <button
            type="button"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-label={drawerOpen ? "Close menu" : "Open menu"}
            aria-expanded={drawerOpen}
            aria-controls="mobile-drawer"
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 hover:bg-brandPink/10 text-brandSlate transition"
            data-testid="nav-hamburger-btn"
          >
            {drawerOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 top-16 bg-slate-900/40 backdrop-blur-sm z-30"
            onClick={() => setDrawerOpen(false)}
            data-testid="nav-drawer-backdrop"
            aria-hidden="true"
          />
          <div
            id="mobile-drawer"
            role="menu"
            className="md:hidden absolute inset-x-0 top-16 z-40 bg-[#FFFDF7] border-b border-slate-200 shadow-pop animate-in slide-in-from-top duration-200"
            data-testid="nav-drawer"
          >
            {user ? (
              <div className="px-6 py-5 flex flex-col gap-3">
                <div className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Signed in</div>
                <div className="text-sm text-slate-700">Hi, <strong className="text-brandSlate">{user.name || user.email}</strong></div>
                <Link to="/dashboard/parent" role="menuitem" className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-brandPink text-slate-900 font-bold text-sm transition" data-testid="nav-drawer-dashboard">
                  <LayoutDashboard className="w-4 h-4 text-brandPink" /> Dashboard
                </Link>
                <Link to="/sell" role="menuitem" className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-brandPink text-slate-900 font-bold text-sm transition" data-testid="nav-drawer-sell">
                  Sell a Toy
                </Link>
                <Link to="/orders" role="menuitem" className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-brandPink text-slate-900 font-bold text-sm transition" data-testid="nav-drawer-orders">
                  Orders
                </Link>
                {/* Iteration 250 — Notifications pill with unread badge
                    inside the hamburger slide-out menu. */}
                <NotificationBell testIdPrefix="nav-drawer-notifications" variant="row" />
                <Link to="/browse" role="menuitem" className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-brandPink text-slate-900 font-bold text-sm transition" data-testid="nav-drawer-browse">
                  <Compass className="w-4 h-4 text-brandTeal" /> Browse
                </Link>
                <button
                  type="button"
                  onClick={async () => { await logout(); setDrawerOpen(false); navigate("/"); }}
                  className="inline-flex items-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-rose-400 text-rose-700 font-bold text-sm transition"
                  data-testid="nav-drawer-logout"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            ) : (
              // Order enforced by spec (iteration 57):
              //   1. Get Started (primary)
              //   2. Parent Sign In
              //   3. Kids Zone Sign In
              //   4. Browse - No Log In Required
              <div className="px-6 py-5 flex flex-col gap-3">
                <Link
                  to="/auth?mode=register"
                  role="menuitem"
                  className="btn-primary w-full justify-center !py-3 text-sm"
                  data-testid="nav-drawer-get-started"
                >
                  Get Started <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/auth?mode=login"
                  role="menuitem"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-brandPink text-slate-900 font-bold text-sm transition"
                  data-testid="nav-drawer-parent-signin"
                >
                  <LogIn className="w-4 h-4 text-brandSlate" /> Parent Sign In
                </Link>
                <Link
                  to="/kidzone"
                  role="menuitem"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-brandPink text-slate-900 font-bold text-sm transition"
                  data-testid="nav-drawer-kidzone-signin"
                >
                  <Gamepad2 className="w-4 h-4 text-brandPink" /> Kids Zone Sign In
                </Link>
                <Link
                  to="/browse"
                  role="menuitem"
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-white border border-slate-200 hover:border-brandTeal text-slate-900 font-bold text-sm transition"
                  data-testid="nav-drawer-browse"
                >
                  <ShoppingBag className="w-4 h-4 text-brandTeal" /> Browse — No Log In Required
                </Link>
              </div>
            )}
          </div>
        </>
      )}
    </header>
  );
}
